import { faDownload, faFileInvoice } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { resolveApiAssetUrl } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useClientInvoices } from '../../hooks';

interface InvoicesTabProps {
  teamId: number;
}

export const InvoicesTab = ({ teamId }: InvoicesTabProps) => {
  const { t, i18n } = useTranslation('viewClientModule');
  const { invoices, total, isLoading } = useClientInvoices(teamId);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'es' ? es : undefined;
      return format(date, 'dd/MM/yyyy', { locale });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FontAwesomeIcon icon={faFileInvoice} className="w-12 h-12 mb-4 opacity-50" />
        <p>{t('tabs.invoices.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('tabs.invoices.total', { count: total })}</p>
      </div>
      <div className="border border-card-border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tabs.invoices.number')}</TableHead>
              <TableHead>{t('tabs.invoices.period')}</TableHead>
              <TableHead>{t('tabs.invoices.date')}</TableHead>
              <TableHead>{t('tabs.invoices.expiredAt')}</TableHead>
              <TableHead className="text-right">{t('tabs.invoices.base')}</TableHead>
              <TableHead className="text-right">{t('tabs.invoices.taxes')}</TableHead>
              <TableHead className="text-right">{t('tabs.invoices.grandTotal')}</TableHead>
              <TableHead className="text-center">{t('tabs.invoices.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const pdfHref = resolveApiAssetUrl(invoice.pdf_url);
              return (
                <TableRow key={invoice.id}>
                  <TableCell className="font-medium">{invoice.erp_number || `#${invoice.id}`}</TableCell>
                  <TableCell>{invoice.period_month || '-'}</TableCell>
                  <TableCell>{formatDate(invoice.date)}</TableCell>
                  <TableCell>{formatDate(invoice.expired_at)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(invoice.base_total)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(invoice.taxes_total)}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(invoice.grand_total)}</TableCell>
                  <TableCell className="text-center">
                    {pdfHref ? (
                      <a
                        href={pdfHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('tabs.invoices.downloadPdf')}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted transition-colors"
                      >
                        <FontAwesomeIcon icon={faDownload} className="w-4 h-4 text-primary" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
